import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import useApi from '../api';
import { formatCredits, formatUsd } from './format';
import type { CreditPlan } from './types';

// Copy follows the positioning rules: credits are units of agent work, never
// tokens or our costs; prepaid and capped is the public frame.
const FEATURES = [
  'Every capability included — conversations, background services, proactive outreach, email, voice',
  'Prepaid and capped: you can never get a surprise bill',
  'Change your amount any time; upgrades apply immediately',
  "Unused monthly credits don't roll over; purchased top-ups never expire",
];

interface PlanPickerProps {
  currentPlanId?: string | null;
  hasSubscription: boolean;
  onSelect: (planId: string) => Promise<void>;
  loading?: boolean;
  /** Small label above the price (the tier's name on the pricing page). */
  title?: string;
  className?: string;
}

const pickDefault = (plans: CreditPlan[], currentPlanId?: string | null): string | null => {
  if (currentPlanId && plans.some((plan) => plan.id === currentPlanId)) return currentPlanId;
  const featured = plans.find((plan) => plan.is_featured);
  return featured?.id ?? plans[0]?.id ?? null;
};

/**
 * One price card with a dropdown of monthly credit amounts (the viktor.com
 * shape). Reads GET /billing/plans so the admin-managed ladder is the only
 * source of truth; nothing about rungs is hard-coded here.
 */
const PlanPicker: React.FC<PlanPickerProps> = ({
  currentPlanId,
  hasSubscription,
  onSelect,
  loading = false,
  title = 'Monthly credits',
  className = '',
}) => {
  const { get } = useApi();
  const [plans, setPlans] = useState<CreditPlan[]>([]);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const response = await get('/billing/plans');
        const rows: CreditPlan[] = Array.isArray(response?.data?.plans) ? response.data.plans : [];
        const monthly = rows
          .filter((plan) => plan.kind === 'subscription')
          .sort((a, b) => a.sort_order - b.sort_order || a.price_cents - b.price_cents);
        if (!cancelled) {
          setPlans(monthly);
          setSelectedId((prev) => prev ?? pickDefault(monthly, currentPlanId));
          setFetchError(null);
        }
      } catch (error) {
        console.error('Error loading credit plans:', error);
        if (!cancelled) setFetchError('Plans are unavailable right now. Please try again shortly.');
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the caller learns the current plan after the list loaded, follow it.
  useEffect(() => {
    if (currentPlanId && plans.some((plan) => plan.id === currentPlanId)) {
      setSelectedId(currentPlanId);
    }
  }, [currentPlanId, plans]);

  const groups = useMemo(() => {
    const ordered: { label: string | null; plans: CreditPlan[] }[] = [];
    for (const plan of plans) {
      const label = plan.group_label || null;
      const last = ordered[ordered.length - 1];
      if (last && last.label === label) last.plans.push(plan);
      else ordered.push({ label, plans: [plan] });
    }
    return ordered;
  }, [plans]);

  const selected = plans.find((plan) => plan.id === selectedId) ?? null;
  const isCurrent = !!selected && !!currentPlanId && selected.id === currentPlanId;

  const buttonLabel = (() => {
    if (!hasSubscription) return 'Get started';
    if (isCurrent) return 'Current plan';
    return 'Switch to this amount';
  })();

  if (fetching) {
    return (
      <div className={`rounded-xl border bg-card p-6 ${className}`}>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-4 h-10 w-40" />
        <Skeleton className="mt-4 h-10 w-full" />
        <Skeleton className="mt-4 h-10 w-full" />
      </div>
    );
  }

  if (fetchError || plans.length === 0) {
    return (
      <div className={`rounded-xl border bg-card p-6 text-sm text-muted-foreground ${className}`}>
        {fetchError || 'No plans are available yet.'}
      </div>
    );
  }

  return (
    <div className={`relative rounded-xl border border-primary/40 bg-card shadow-[0_0_0_1px] shadow-primary/10 ${className}`}>
      {selected?.is_featured && (
        <div className="absolute -top-3 left-6">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm">
            <Sparkles className="h-3 w-3" />
            Most popular
          </span>
        </div>
      )}

      <div className="p-6">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>

        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-4xl font-bold tracking-tight">
            {selected ? formatUsd(selected.price_cents, selected.currency) : '—'}
          </span>
          <span className="text-sm text-muted-foreground">/ month</span>
        </div>

        <div className="mt-4">
          <Select value={selectedId ?? undefined} onValueChange={setSelectedId} disabled={loading}>
            <SelectTrigger className="h-11 w-full">
              <SelectValue>
                {selected ? `${formatCredits(selected.credits)} credits monthly` : 'Choose an amount'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {groups.map((group, index) => (
                <SelectGroup key={group.label ?? `group-${index}`}>
                  {group.label && <SelectLabel>{group.label}</SelectLabel>}
                  {group.plans.map((plan) => (
                    <SelectItem
                      key={plan.id}
                      value={plan.id}
                      textValue={`${formatCredits(plan.credits)} credits monthly`}
                    >
                      <span className="flex w-full items-center justify-between gap-6">
                        <span className="flex items-center gap-2">
                          {formatCredits(plan.credits)} credits monthly
                          {plan.is_featured && (
                            <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-medium">
                              Most popular
                            </Badge>
                          )}
                          {plan.id === currentPlanId && (
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-medium">
                              Current
                            </Badge>
                          )}
                        </span>
                        <span className="tabular-nums text-muted-foreground">
                          {formatUsd(plan.price_cents, plan.currency)}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-2 min-h-[1rem] text-xs text-muted-foreground">
            {selected && selected.bonus_pct > 0
              ? `Includes ${selected.bonus_pct % 1 === 0 ? selected.bonus_pct.toFixed(0) : selected.bonus_pct}% bonus credits`
              : ''}
          </p>
        </div>

        <Button
          type="button"
          className="mt-4 h-11 w-full text-sm font-medium"
          disabled={!selected || isCurrent || loading}
          onClick={() => {
            if (selected && !isCurrent) void onSelect(selected.id);
          }}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Working…
            </>
          ) : (
            buttonLabel
          )}
        </Button>

        <ul className="mt-6 space-y-2.5 border-t pt-5">
          {FEATURES.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
              <span className="text-sm leading-snug text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PlanPicker;
